import React from 'react';
import { Input } from 'reactstrap';
import { toast } from 'react-toastify';
import EmployeeService from '../../../services/Employee';
import PerformanceOneOnOneService from '../../../services/PerformanceOneOnOne';
import PolicyViolationsService from '../../../services/PolicyViolations';
import { FilteredOptionsInterface } from '../../../entities/FilteredOptions';
import { createOptionsPage } from '../../../entities/OptionsPaged';
import { createPagedMeta } from '../../../entities/PagedMeta';
import { CORE_VALUES } from '../Staff/PerformanceManagement/OneOnOne/Form/formConstants';

export interface OneOnOneImportData {
  employeesList: any[];
  policyViolationsList: any[];
}

export const loadOneOnOneData = async (token: string, schoolId: string): Promise<OneOnOneImportData> => {
  const employeesList = await loadEmployees(token, schoolId);
  const policyViolationsList = await loadPolicyViolations(token);

  return {
    employeesList,
    policyViolationsList,
  };
};

const loadEmployees = async (token: string, schoolId: string): Promise<any[]> => {
  try {
    const filteredOptions: FilteredOptionsInterface = {
      filters: [],
      options: createOptionsPage(createPagedMeta()),
    };
    filteredOptions.options.itemsPerPage = 100000;
    const result = await EmployeeService(token).listEmployeesBySchool(schoolId, '', filteredOptions);
    const employeesWithUsers = (result.data || []).filter((emp: any) => emp.user);
    return employeesWithUsers || [];
  } catch (e: any) {
    toast.error(e?.response?.data?.message || 'No employees found');
    return [];
  }
};

const loadPolicyViolations = async (token: string): Promise<any[]> => {
  try {
    const result = await PolicyViolationsService(token).list();
    return result.data || [];
  } catch (e: any) {
    toast.error(e?.response?.data?.message || 'No policy violations found');
    return [];
  }
};

export interface ProcessedOneOnOne {
  userId: string;
  name: string;
  dateOfIncident: string;
  position: string;
  department: string;
  supervisor: string;
  dateOfHire: string;
  policyViolationIds: string[];
  descriptionOfOneOnOne: string;
  coreValues: string[];
  meetingGoal: string;
  privateNotes: string;
  approved: boolean;
}

export interface ProcessingErrors {
  [key: string]: string;
}

export const processOneOnOneImport = (
  importedLines: any[],
  formData: any[],
  employeesList: any[],
  policyViolationsList: any[],
): { oneOnOnes: ProcessedOneOnOne[]; errors: ProcessingErrors } => {
  const oneOnOnes: ProcessedOneOnOne[] = [];
  const errors: ProcessingErrors = {};

  for (let i = 0; i < importedLines.length; i++) {
    const line = importedLines[i];
    const formLine = formData[i] || {};

    const firstName = formLine['first_name'] || line['first_name'] || '';
    const lastName = formLine['last_name'] || line['last_name'] || '';
    const staffNameOrId =
      formLine['staff_name'] ||
      formLine['staffname'] ||
      line['staff_name'] ||
      line['staffname'] ||
      formLine['staff'] ||
      line['staff'] ||
      `${firstName} ${lastName}`.trim() ||
      '';
    const dateOfIncident =
      formLine['date_of_incident'] ||
      formLine['dateofincident'] ||
      line['date_of_incident'] ||
      line['dateofincident'] ||
      formLine['date'] ||
      line['date'] ||
      '';
    const policyViolationsStr =
      formLine['policy_violations'] ||
      formLine['policyviolations'] ||
      line['policy_violations'] ||
      line['policyviolations'] ||
      formLine['policy_violation'] ||
      line['policy_violation'] ||
      '';
    const descriptionOfOneOnOne =
      formLine['description_of_one_on_one'] ||
      formLine['descriptionofoneonone'] ||
      line['description_of_one_on_one'] ||
      line['descriptionofoneonone'] ||
      formLine['discussion'] ||
      line['discussion'] ||
      '';
    const coreValueStr =
      formLine['core_value'] || formLine['corevalue'] || line['core_value'] || line['corevalue'] || '';
    const meetingGoal =
      formLine['meeting_goal'] || formLine['meetinggoal'] || line['meeting_goal'] || line['meetinggoal'] || '';
    const meetingGoalFromCsv = formLine['goal'] || line['goal'] || '';
    const privateNotes =
      formLine['private_notes'] || formLine['privatenotes'] || line['private_notes'] || line['privatenotes'] || '';
    const training = formLine['training'] || line['training'] || '';

    let employee = employeesList.find((emp: any) => emp.user?.id === staffNameOrId);
    if (!employee) {
      employee = employeesList.find((emp: any) => {
        const fullName = `${emp.user?.firstName || ''} ${emp.user?.lastName || ''}`.trim();
        return fullName.toLowerCase() === String(staffNameOrId).toLowerCase().trim();
      });
    }

    if (!employee || !employee.user) {
      errors[`${i}.userId`] = `Staff member "${staffNameOrId}" not found`;
      continue;
    }

    const cleanedPolicyViolationsStr = String(policyViolationsStr || '').trim();
    const lowerPolicyViolations = cleanedPolicyViolationsStr.toLowerCase();
    const isNonePolicyViolation =
      cleanedPolicyViolationsStr.length === 0 ||
      lowerPolicyViolations === 'none' ||
      lowerPolicyViolations.startsWith('none ') ||
      lowerPolicyViolations.startsWith('none |');

    const policyViolationNames = isNonePolicyViolation
      ? []
      : cleanedPolicyViolationsStr
          .split(/[,;\r\n]+/)
          .map((name: string) => name.trim())
          .filter((name: string) => name.length > 0);

    const policyViolationIds: string[] = [];
    const notFoundViolations: string[] = [];

    for (const pvName of policyViolationNames) {
      const pv = policyViolationsList.find((pv: any) => pv.name.toLowerCase() === pvName.toLowerCase());
      if (pv) {
        policyViolationIds.push(pv.id);
      } else {
        notFoundViolations.push(pvName);
      }
    }

    if (notFoundViolations.length > 0) {
      errors[`${i}.policyViolations`] = `Policy violation${
        notFoundViolations.length > 1 ? 's' : ''
      } not found: ${notFoundViolations.join(', ')}`;
    }

    if (policyViolationIds.length === 0 && policyViolationNames.length > 0) {
      if (!errors[`${i}.policyViolations`]) {
        errors[`${i}.policyViolations`] = 'No valid policy violations found';
      }
    }

    const coreValueNames = coreValueStr
      .split(/[,;]/)
      .map((name: string) => name.trim())
      .filter((name: string) => name.length > 0);

    const coreValues: string[] = [];
    const notFoundCoreValues: string[] = [];

    for (const cvName of coreValueNames) {
      const cv = CORE_VALUES.find(
        (cv: any) => cv.label.toLowerCase() === cvName.toLowerCase() || cv.id.toLowerCase() === cvName.toLowerCase(),
      );
      if (cv) {
        coreValues.push(cv.id);
      } else {
        notFoundCoreValues.push(cvName);
      }
    }

    if (notFoundCoreValues.length > 0) {
      errors[`${i}.coreValues`] = `Core value${
        notFoundCoreValues.length > 1 ? 's' : ''
      } not found: ${notFoundCoreValues.join(', ')}. Valid values: ${CORE_VALUES.map((cv) => cv.label).join(', ')}`;
    }

    if (!dateOfIncident) {
      errors[`${i}.dateOfIncident`] = 'Date of Incident is required';
    }

    if (!descriptionOfOneOnOne.trim()) {
      errors[`${i}.descriptionOfOneOnOne`] = 'Description of One on One is required';
    }

    const finalMeetingGoal = String(meetingGoal || meetingGoalFromCsv || '').trim();
    if (!finalMeetingGoal) {
      errors[`${i}.meetingGoal`] = 'Meeting Goal is required';
    }

    if (Object.keys(errors).some((key) => key.startsWith(`${i}.`))) {
      continue;
    }

    const finalPrivateNotes = [
      String(privateNotes || '').trim(),
      training ? `Training: ${String(training).trim()}` : '',
    ]
      .filter((v) => v.length > 0)
      .join('\n');

    oneOnOnes.push({
      userId: employee.user.id,
      name: `${employee.user.firstName} ${employee.user.lastName}`,
      dateOfIncident,
      position: employee.title?.name || '',
      department: employee.department?.name || '',
      supervisor: employee.supervisor?.name || '',
      dateOfHire: employee.startDate || '2024-01-01',
      policyViolationIds,
      descriptionOfOneOnOne,
      coreValues,
      meetingGoal: finalMeetingGoal,
      privateNotes: finalPrivateNotes,
      approved: true,
    });
  }

  return { oneOnOnes, errors };
};

export const submitOneOnOneImport = async (
  token: string,
  schoolId: string,
  oneOnOnes: ProcessedOneOnOne[],
): Promise<void> => {
  const service = PerformanceOneOnOneService(token);
  await service.create({ schoolId, oneOnOnes, isImport: true });
};

export const getOneOnOneFieldConfig = (
  value: string,
  name: string,
  employeesList: any[],
  _policyViolationsList: any[],
): JSX.Element | null => {
  if (
    name.split('][')[1].toLocaleLowerCase() === 'staff_name]' ||
    name.split('][')[1].toLocaleLowerCase() === 'staffname]'
  ) {
    const selected = employeesList.find((emp: any) => {
      const fullName = `${emp.user?.firstName || ''} ${emp.user?.lastName || ''}`.trim();
      return fullName.toLowerCase() === String(value).toLowerCase().trim() || emp.user?.id === value;
    });
    const defaultValue = selected?.user?.id || value || '';
    return (
      <Input type="select" defaultValue={defaultValue} name={name}>
        <option value={''}></option>
        {employeesList.map((emp: any) => {
          const fullName = `${emp.user?.firstName || ''} ${emp.user?.lastName || ''}`.trim();
          return (
            <option key={emp.user?.id} value={emp.user?.id}>
              {fullName}
            </option>
          );
        })}
      </Input>
    );
  }

  if (
    name.split('][')[1].toLocaleLowerCase() === 'policy_violations]' ||
    name.split('][')[1].toLocaleLowerCase() === 'policyviolations]'
  ) {
    return <Input type="text" defaultValue={value} name={name} placeholder="Separate multiple with comma" />;
  }

  if (
    name.split('][')[1].toLocaleLowerCase() === 'core_value]' ||
    name.split('][')[1].toLocaleLowerCase() === 'corevalue]'
  ) {
    return (
      <Input
        type="text"
        defaultValue={value}
        name={name}
        placeholder={`Separate multiple with comma. Valid values: ${CORE_VALUES.map((cv) => cv.label).join(', ')}`}
      />
    );
  }

  return null;
};
